import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useGetWebsitesQuery, useGetWebsitesByTiersQuery } from "../../app/api/platform/platformWebsitesApiSlice";
import {
  useGetWebsitesAnalyticsScansQuery,
  useLazyGetScanScopeUrlsQuery,
} from "../../app/api/platform/platformApiSlice";
import { useGetAgenticInFlightQuery } from "../../app/api/console/agenticRunsApiSlice";
import { PAYING_TIERS, PLAN_DISPLAY, PLAN_STYLES, displayPlan } from "../../constants/customers";

const AGENT_LINE_COLORS = [
  "#8B5CF6", "#EC4899", "#1FAF5C", "#FF7B43", "#3B82F6",
  "#F59E0B", "#10B981", "#EF4444", "#06B6D4", "#A855F7",
  "#F472B6", "#84CC16", "#F97316", "#0EA5E9", "#D946EF",
];

function AgentAnalytics() {
  const [timeRange, setTimeRange] = useState("30");

  // Fetch paying customer IDs from API
  const { data: payingCustomers = [] } = useGetWebsitesByTiersQuery({ tiers: PAYING_TIERS });
  const payingWebsiteIds = useMemo(() => payingCustomers.map((c) => c.website_id), [payingCustomers]);

  // Website filter state — initialized empty, set once API data loads
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState(new Set());
  const [websiteSearchTerm, setWebsiteSearchTerm] = useState("");
  const [isWebsiteFilterOpen, setIsWebsiteFilterOpen] = useState(false);
  const websiteFilterRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (payingWebsiteIds.length > 0 && !initializedRef.current) {
      setSelectedWebsiteIds(new Set(payingWebsiteIds));
      initializedRef.current = true;
    }
  }, [payingWebsiteIds]);

  // Expanded agent state
  const [expandedAgentKey, setExpandedAgentKey] = useState(null);

  // All Agent Statistics view mode: "timeseries" (default) or "table"
  const [statsView, setStatsView] = useState("timeseries");
  const [visibleAgentKeys, setVisibleAgentKeys] = useState(null); // null = uninitialized; defaults to top-N once data loads
  const [agentSearchTerm, setAgentSearchTerm] = useState("");
  const [hoveredAgentKey, setHoveredAgentKey] = useState(null);
  const [expandedTableAgentKeys, setExpandedTableAgentKeys] = useState(new Set());
  const TOP_N_DEFAULT = 10;

  const toggleTableAgentExpansion = (key) => {
    setExpandedTableAgentKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // In-progress tracker collapsed state (collapsed by default)
  const [expandedInProgressWebsites, setExpandedInProgressWebsites] = useState(new Set());

  // Lazy query for fetching scope URLs
  const [getScopeUrls] = useLazyGetScanScopeUrlsQuery();

  // State to track which scan is downloading
  const [downloadingScanId, setDownloadingScanId] = useState(null);

  // Download scope URLs as TXT file
  const handleDownloadUrls = useCallback(
    async (scanId, scanTitle) => {
      setDownloadingScanId(scanId);
      try {
        const result = await getScopeUrls({ scanId, useProd: true }).unwrap();
        if (result.urls && result.urls.length > 0) {
          const content = result.urls.join("\n");
          const blob = new Blob([content], { type: "text/plain" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${scanTitle || scanId}_urls.txt`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Failed to download URLs:", error);
        alert("Failed to download URLs");
      } finally {
        setDownloadingScanId(null);
      }
    },
    [getScopeUrls]
  );

  const toggleInProgressWebsite = (websiteId) => {
    setExpandedInProgressWebsites((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(websiteId)) {
        newSet.delete(websiteId);
      } else {
        newSet.add(websiteId);
      }
      return newSet;
    });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (websiteFilterRef.current && !websiteFilterRef.current.contains(event.target)) {
        setIsWebsiteFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Step 1: Fetch all websites to get IDs and for the filter dropdown
  const { data: websitesData = [], isLoading: isLoadingWebsites } = useGetWebsitesQuery({ useProd: true });

  // Extract all website IDs
  const allWebsiteIds = useMemo(() => {
    return websitesData.map((w) => w.website_id);
  }, [websitesData]);

  // Map website IDs to their subscription plan
  const planByWebsiteId = useMemo(() => {
    const map = {};
    websitesData.forEach((w) => {
      map[w.website_id] = w.subscription_plan;
    });
    return map;
  }, [websitesData]);

  // Step 2: Fetch analytics scans for all websites
  const {
    data: analyticsData = [],
    isLoading: isLoadingScans,
    error,
    refetch,
  } = useGetWebsitesAnalyticsScansQuery(
    { websiteIds: allWebsiteIds, useProd: true },
    { skip: allWebsiteIds.length === 0 }
  );

  // Live running agents come from the platform V2 activity_runs table (the
  // same source the platform's own in-flight UI reads), exposed by the console
  // backend at /agentic-runs/in-flight. Prod only.
  const { data: inFlightData } = useGetAgenticInFlightQuery(
    { env: "prod" },
    { pollingInterval: 30000 }
  );
  const runningAgenticRuns = useMemo(
    () => inFlightData?.runs || [],
    [inFlightData]
  );

  // Build websites list from analytics data (has website_name)
  const websites = useMemo(() => {
    return analyticsData.map((w) => ({
      website_id: w.website_id,
      name: w.website_name,
      website_name: w.website_name,
    }));
  }, [analyticsData]);

  // Flatten all scans from analytics data
  const scans = useMemo(() => {
    return analyticsData.flatMap((website) =>
      (website.scans || []).map((scan) => ({
        ...scan,
        website_id: website.website_id,
        website_name: website.website_name,
      }))
    );
  }, [analyticsData]);

  const isLoading = isLoadingWebsites || isLoadingScans;

  // Filter and sort websites for the dropdown (selected first, then by search)
  const filteredWebsites = useMemo(() => {
    let filtered = websites;

    // Filter by search term
    if (websiteSearchTerm) {
      const term = websiteSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (w) =>
          w.website_id.toLowerCase().includes(term) ||
          (w.name && w.name.toLowerCase().includes(term)) ||
          (w.website_name && w.website_name.toLowerCase().includes(term))
      );
    }

    // Sort: selected websites first, then alphabetically by name
    return [...filtered].sort((a, b) => {
      const aSelected = selectedWebsiteIds.has(a.website_id);
      const bSelected = selectedWebsiteIds.has(b.website_id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      // Then sort by name
      const aName = a.name || a.website_name || a.website_id;
      const bName = b.name || b.website_name || b.website_id;
      return aName.localeCompare(bName);
    });
  }, [websites, websiteSearchTerm, selectedWebsiteIds]);

  // Toggle website selection
  const toggleWebsite = (websiteId) => {
    setSelectedWebsiteIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(websiteId)) {
        newSet.delete(websiteId);
      } else {
        newSet.add(websiteId);
      }
      return newSet;
    });
  };

  // Select/deselect all
  const selectAllWebsites = () => {
    setSelectedWebsiteIds(new Set(websites.map((w) => w.website_id)));
  };

  const deselectAllWebsites = () => {
    setSelectedWebsiteIds(new Set());
  };

  const selectPayingOnly = () => {
    setSelectedWebsiteIds(new Set(payingWebsiteIds));
  };

  const selectTier = (tier) => {
    const tierWebsiteIds = websitesData
      .filter((w) => w.subscription_plan === tier)
      .map((w) => w.website_id);
    setSelectedWebsiteIds(new Set(tierWebsiteIds));
  };

  // Filter scans by website and time range
  const filteredScans = useMemo(() => {
    let filtered = scans;

    // Filter by selected websites
    if (selectedWebsiteIds.size > 0) {
      filtered = filtered.filter((scan) => selectedWebsiteIds.has(scan.website_id));
    }

    // Filter by time range
    if (timeRange === "all") return filtered;

    const days = parseInt(timeRange, 10);
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);

    return filtered.filter((scan) => {
      const dateValue = scan.start_time || scan.created_at || scan.date_added;
      if (!dateValue) return true;

      const scanDate = new Date(dateValue);
      if (isNaN(scanDate.getTime())) return true;

      return scanDate >= cutoff;
    });
  }, [scans, timeRange, selectedWebsiteIds]);

  // Aggregate scan data by scan_template_id or title
  const agentStats = useMemo(() => {
    const stats = {};

    filteredScans.forEach((scan) => {
      // Use scan_template_id as the primary grouping key, fall back to title
      const agentKey = scan.scan_template_id || scan.title || "Unknown Agent";
      const agentTitle = scan.title || scan.scan_template_id || "Unknown Agent";

      if (!stats[agentKey]) {
        stats[agentKey] = {
          key: agentKey,
          title: agentTitle,
          count: 0,
          latestRun: null,
          scans: [],
          websites: new Set(),
        };
      }

      stats[agentKey].count += 1;
      stats[agentKey].scans.push(scan);
      if (scan.website_id) {
        stats[agentKey].websites.add(scan.website_id);
      }

      const scanDate = new Date(scan.start_time || scan.created_at || scan.date_added);
      if (!stats[agentKey].latestRun || scanDate > stats[agentKey].latestRun) {
        stats[agentKey].latestRun = scanDate;
      }
    });

    // Convert to array and sort by count (most popular first)
    return Object.values(stats)
      .map((stat) => ({
        ...stat,
        websiteCount: stat.websites.size,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredScans]);

  // Get recently launched agents (last 7 days)
  const recentAgents = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return [...filteredScans]
      .filter((scan) => {
        const scanDate = new Date(scan.start_time || scan.created_at);
        return scanDate >= sevenDaysAgo;
      })
      .sort((a, b) => {
        const dateA = new Date(a.start_time || a.created_at);
        const dateB = new Date(b.start_time || b.created_at);
        return dateB - dateA;
      })
      .slice(0, 10);
  }, [filteredScans]);

  // Get running agents grouped by website, scoped to the websites selected in
  // the top filter (paying customers by default). Maps the in-flight run shape
  // onto the render shape the tracker JSX expects.
  const inProgressScans = useMemo(() => {
    const grouped = {};
    runningAgenticRuns.forEach((run) => {
      const websiteId = run.website_id;
      if (!websiteId) return;
      // Respect the top website filter (the "41 selected" set).
      if (selectedWebsiteIds.size > 0 && !selectedWebsiteIds.has(websiteId)) return;

      if (!grouped[websiteId]) {
        grouped[websiteId] = {
          website_id: websiteId,
          website_name: run.website_name || websiteId,
          scans: [],
        };
      }
      grouped[websiteId].scans.push({
        scan_id: run.run_id,
        title: run.title || run.agent_id || "Agent Run",
        start_time: run.started_at,
        website_id: websiteId,
        website_name: run.website_name || websiteId,
      });
    });

    // Sort scans within each group by start_time (oldest first - longest running)
    Object.values(grouped).forEach((group) => {
      group.scans.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    });

    return Object.values(grouped).sort((a, b) =>
      (a.website_name || "").localeCompare(b.website_name || "")
    );
  }, [runningAgenticRuns, selectedWebsiteIds]);

  // Calculate time elapsed since scan started
  const getElapsedTime = (startTime) => {
    if (!startTime) return "Unknown";
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    const uniqueAgents = agentStats.length;
    const totalScans = filteredScans.length;
    const uniqueWebsites = new Set(filteredScans.map((s) => s.website_id).filter(Boolean)).size;
    const avgScansPerAgent = uniqueAgents > 0 ? (totalScans / uniqueAgents).toFixed(1) : 0;

    return { uniqueAgents, totalScans, uniqueWebsites, avgScansPerAgent };
  }, [agentStats, filteredScans]);

  // Agents to plot in time series: exclude any with fewer than 2 runs
  const plottedAgents = useMemo(
    () => agentStats.filter((a) => a.count >= 2),
    [agentStats]
  );

  // Determine bucket size based on time range
  const bucketGranularity = useMemo(() => {
    if (timeRange === "7") return "day";
    if (timeRange === "30") return "day";
    if (timeRange === "90") return "week";
    return "week"; // all time
  }, [timeRange]);

  // Build time series data: rows keyed by bucket date, columns per agent
  const timeSeriesData = useMemo(() => {
    if (plottedAgents.length === 0) return [];

    const bucketKey = (date) => {
      const d = new Date(date);
      if (isNaN(d.getTime())) return null;
      if (bucketGranularity === "day") {
        return d.toISOString().slice(0, 10);
      }
      // week: floor to Monday
      const day = d.getUTCDay();
      const diff = (day + 6) % 7;
      const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - diff));
      return monday.toISOString().slice(0, 10);
    };

    const plottedKeys = new Set(plottedAgents.map((a) => a.key));
    const buckets = new Map();

    filteredScans.forEach((scan) => {
      const agentKey = scan.scan_template_id || scan.title || "Unknown Agent";
      if (!plottedKeys.has(agentKey)) return;
      const bk = bucketKey(scan.start_time || scan.created_at || scan.date_added);
      if (!bk) return;
      if (!buckets.has(bk)) buckets.set(bk, { date: bk });
      const row = buckets.get(bk);
      row[agentKey] = (row[agentKey] || 0) + 1;
    });

    const sorted = Array.from(buckets.values()).sort((a, b) => a.date.localeCompare(b.date));
    const running = {};
    plottedAgents.forEach((a) => {
      running[a.key] = 0;
    });
    return sorted.map((row) => {
      const filled = { date: row.date };
      plottedAgents.forEach((a) => {
        running[a.key] += row[a.key] || 0;
        filled[a.key] = running[a.key];
      });
      return filled;
    });
  }, [filteredScans, plottedAgents, bucketGranularity]);

  // Initialize visibility to top-N once plottedAgents are known, and re-seed
  // whenever the underlying set of plottable agents changes (filters/time range).
  const plottedKeySignature = useMemo(
    () => plottedAgents.map((a) => a.key).join("|"),
    [plottedAgents]
  );
  useEffect(() => {
    if (plottedAgents.length === 0) {
      setVisibleAgentKeys(new Set());
      return;
    }
    setVisibleAgentKeys(new Set(plottedAgents.slice(0, TOP_N_DEFAULT).map((a) => a.key)));
  }, [plottedKeySignature, plottedAgents]);

  const isAgentVisible = (key) =>
    visibleAgentKeys === null ? false : visibleAgentKeys.has(key);

  const toggleAgentVisibility = (key) => {
    setVisibleAgentKeys((prev) => {
      const next = new Set(prev || []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const showTopN = () => {
    setVisibleAgentKeys(new Set(plottedAgents.slice(0, TOP_N_DEFAULT).map((a) => a.key)));
  };

  const showAllAgents = () => {
    setVisibleAgentKeys(new Set(plottedAgents.map((a) => a.key)));
  };

  const hideAllAgents = () => {
    setVisibleAgentKeys(new Set());
  };

  // Stable color per agent key (based on rank in plottedAgents), so toggling
  // visibility doesn't shuffle colors.
  const agentColorByKey = useMemo(() => {
    const map = {};
    plottedAgents.forEach((a, i) => {
      map[a.key] = AGENT_LINE_COLORS[i % AGENT_LINE_COLORS.length];
    });
    return map;
  }, [plottedAgents]);

  const filteredAgentChips = useMemo(() => {
    const term = agentSearchTerm.trim().toLowerCase();
    if (!term) return plottedAgents;
    return plottedAgents.filter(
      (a) =>
        a.title.toLowerCase().includes(term) || a.key.toLowerCase().includes(term)
    );
  }, [plottedAgents, agentSearchTerm]);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRelativeTime = (date) => {
    if (!date) return "N/A";
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return "Just now";
  };

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-h2 text-text-primary'>Agent Analytics</h1>
        <p className='text-body-sm text-text-secondary mt-1'>Monitor agent usage, popularity, and recent activity across production.</p>
      </div>

      {/* Filters */}
      <div className='flex flex-wrap gap-3 items-end justify-between'>
        <div className='flex flex-wrap gap-3 items-end'>
          <div>
            <label className='block text-body-xs font-medium text-text-secondary mb-1.5'>Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className='px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent-purple/20'>
                <option value='7'>Last 7 days</option>
                <option value='30'>Last 30 days</option>
                <option value='90'>Last 90 days</option>
                <option value='all'>All time</option>
              </select>
            </div>

            {/* Website Filter Dropdown */}
            <div className='relative' ref={websiteFilterRef}>
              <label className='block text-body-xs font-medium text-text-secondary mb-1.5'>
                Websites ({selectedWebsiteIds.size} selected)
              </label>
              <button
                onClick={() => setIsWebsiteFilterOpen(!isWebsiteFilterOpen)}
                className='flex items-center gap-2 px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl hover:bg-surface-neutral focus:outline-none focus:ring-2 focus:ring-accent-purple/20 min-w-[200px] justify-between text-text-secondary'>
                <span className='truncate'>
                  {selectedWebsiteIds.size === 0
                    ? "No websites selected"
                    : selectedWebsiteIds.size === websites.length
                    ? "All websites"
                    : `${selectedWebsiteIds.size} website${selectedWebsiteIds.size !== 1 ? "s" : ""}`}
                </span>
                <svg
                  className={`h-4 w-4 transition-transform ${isWebsiteFilterOpen ? "rotate-180" : ""}`}
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
              </button>

              {/* Dropdown Panel */}
              {isWebsiteFilterOpen && (
                <div className='absolute z-50 mt-2 w-96 bg-white border border-border-subtle rounded-2xl shadow-lg overflow-hidden animate-fade-in'>
                  {/* Search */}
                  <div className='p-3 border-b border-border-subtle'>
                    <input
                      type='text'
                      placeholder='Search websites...'
                      value={websiteSearchTerm}
                      onChange={(e) => setWebsiteSearchTerm(e.target.value)}
                      className='w-full px-3 py-2 text-body-xs bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary'
                    />
                  </div>

                  {/* Quick Actions */}
                  <div className='p-2 border-b border-border-subtle flex gap-1.5 flex-wrap'>
                    <button
                      onClick={selectPayingOnly}
                      className='px-2 py-1 text-caption font-semibold bg-accent-purple/10 text-accent-purple rounded-md hover:bg-accent-purple/20 transition-colors'>
                      All Paying
                    </button>
                    {PAYING_TIERS.map((tier) => (
                      <button
                        key={tier}
                        onClick={() => selectTier(tier)}
                        className={`px-2 py-1 text-caption font-semibold rounded-md hover:opacity-80 transition-opacity ${PLAN_STYLES[tier]}`}>
                        {PLAN_DISPLAY[tier]}
                      </button>
                    ))}
                    <div className='w-full h-px bg-border-subtle my-1'></div>
                    <button
                      onClick={selectAllWebsites}
                      className='px-2 py-1 text-caption font-semibold bg-accent-green/10 text-accent-green rounded-md hover:bg-accent-green/20 transition-colors'>
                      Select All
                    </button>
                    <button
                      onClick={deselectAllWebsites}
                      className='px-2 py-1 text-caption font-semibold bg-surface-neutral-subtle text-text-secondary rounded-md hover:bg-surface-neutral transition-colors'>
                      Clear All
                    </button>
                  </div>

                  {/* Website List */}
                  <div className='max-h-64 overflow-y-auto p-2 scrollbar-thin'>
                    {filteredWebsites.length === 0 ? (
                      <p className='text-text-tertiary text-body-xs text-center py-4'>No websites found</p>
                    ) : (
                      filteredWebsites.map((website) => {
                        const isSelected = selectedWebsiteIds.has(website.website_id);
                        const displayName = website.name || website.website_name || website.website_id;
                        const plan = planByWebsiteId[website.website_id];

                        return (
                          <label
                            key={website.website_id}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-surface-neutral transition-colors ${
                              isSelected ? "border-l-2 border-accent-purple bg-accent-purple/[0.03]" : ""
                            }`}>
                            <input
                              type='checkbox'
                              checked={isSelected}
                              onChange={() => toggleWebsite(website.website_id)}
                              className='rounded border-border-default text-accent-purple focus:ring-accent-purple/30'
                            />
                            <div className='flex-1 min-w-0'>
                              <p className='text-body-xs font-medium text-text-primary truncate flex items-center gap-2'>
                                {displayName}
                                {plan && (
                                  <span className={`px-1.5 py-0.5 text-caption uppercase font-bold rounded-md ${PLAN_STYLES[displayPlan(plan)] || "bg-surface-neutral-subtle text-text-secondary"}`}>
                                    {PLAN_DISPLAY[displayPlan(plan)] || displayPlan(plan)}
                                  </span>
                                )}
                              </p>
                              <p className='text-caption text-text-tertiary truncate'>{website.website_id}</p>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>

                  {/* Close button */}
                  <div className='p-2 border-t border-border-subtle'>
                    <button
                      onClick={() => setIsWebsiteFilterOpen(false)}
                      className='w-full px-3 py-2 text-body-xs font-medium bg-surface-neutral text-text-secondary rounded-lg hover:bg-surface-neutral-subtle transition-colors'>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => refetch()}
            className='flex items-center gap-2 px-4 py-2 text-body-xs font-semibold bg-button-primary text-white rounded-xl hover:bg-button-primary-hover transition-colors'>
            <svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              />
            </svg>
            Refresh
          </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className='flex items-center justify-center py-12'>
          <div className='flex items-center text-blue-600'>
            <svg
              className='animate-spin -ml-1 mr-3 h-8 w-8'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'>
              <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
            </svg>
            <span className='text-lg'>Loading scan data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-6 mb-6'>
          <div className='flex items-center'>
            <svg className='h-5 w-5 text-red-500 mr-2' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <span className='text-red-700'>Failed to load scan data. Please try again.</span>
          </div>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Summary Cards */}
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
            {[
              { label: "Total Scans", value: summaryStats.totalScans.toLocaleString(), color: "#8B5CF6", icon: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" },
              { label: "Unique Agents", value: summaryStats.uniqueAgents, color: "#EC4899", icon: "M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" },
              { label: "Websites Covered", value: summaryStats.uniqueWebsites, color: "#1FAF5C", icon: "M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" },
              { label: "Avg Scans/Agent", value: summaryStats.avgScansPerAgent, color: "#FF7B43", icon: "M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" },
            ].map((card) => (
              <div key={card.label} className='bg-white rounded-2xl border border-border-subtle p-5'>
                <p className='text-body-xs font-medium text-text-secondary mb-1'>{card.label}</p>
                <p className='text-metric' style={{ color: card.color }}>{card.value}</p>
              </div>
            ))}
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            {/* Most Popular Agents */}
            <div className='bg-white rounded-2xl border border-border-subtle p-6'>
              <h2 className='text-body-lg font-semibold text-text-primary mb-4 flex items-center'>
                Most Popular Agents
                <span className='ml-2 text-caption text-text-tertiary font-normal'>(click to expand)</span>
              </h2>
              <div className='space-y-2'>
                {agentStats.slice(0, 10).map((agent, index) => {
                  const isExpanded = expandedAgentKey === agent.key;
                  return (
                    <div key={agent.key} className='rounded-lg overflow-hidden'>
                      {/* Agent Header - Clickable */}
                      <button
                        onClick={() => setExpandedAgentKey(isExpanded ? null : agent.key)}
                        className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors ${
                          isExpanded ? "bg-accent-purple/5 ring-1 ring-accent-purple/20" : "bg-surface-neutral hover:bg-surface-neutral-subtle"
                        }`}>
                        <div className='flex items-center'>
                          <span
                            className={`w-6 h-6 flex items-center justify-center rounded-lg text-caption font-bold mr-3 ${
                              index === 0
                                ? "bg-accent-purple/15 text-accent-purple"
                                : index === 1
                                ? "bg-accent-pink/10 text-accent-pink"
                                : index === 2
                                ? "bg-accent-green/10 text-accent-green"
                                : "bg-surface-neutral-subtle text-text-tertiary"
                            }`}>
                            {index + 1}
                          </span>
                          <div className='text-left'>
                            <p className='font-medium text-text-primary text-body-xs truncate max-w-[180px]'>{agent.title}</p>
                            <p className='text-caption text-text-tertiary'>
                              {agent.websiteCount} website{agent.websiteCount !== 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>
                        <div className='flex items-center gap-2'>
                          <div className='text-right'>
                            <p className='font-bold text-accent-purple'>{agent.count}</p>
                            <p className='text-caption text-text-tertiary'>scans</p>
                          </div>
                          <svg
                            className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                          </svg>
                        </div>
                      </button>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className='bg-white border-t border-border-subtle rounded-b-xl p-3 max-h-64 overflow-y-auto scrollbar-thin'>
                          <p className='text-caption font-semibold text-text-tertiary mb-2 uppercase tracking-wide'>
                            Scan History ({agent.scans.length})
                          </p>
                          <div className='space-y-1'>
                            {agent.scans
                              .sort((a, b) => new Date(b.start_time || b.created_at) - new Date(a.start_time || a.created_at))
                              .map((scan, scanIndex) => {
                                const scanId = scan.scan_id || scan.id;
                                return (
                                  <div key={scanId || scanIndex} className='flex items-center justify-between p-2 bg-surface-neutral rounded-lg text-body-xs gap-2'>
                                    <div className='min-w-0 flex-1'>
                                      <p className='font-medium text-text-primary truncate'>{scan.website_name}</p>
                                      {scanId ? (
                                        <a
                                          href={`https://hub.zenyt.ai/scans/${scanId}`}
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          onClick={(e) => e.stopPropagation()}
                                          className='text-caption font-mono text-accent-purple hover:underline truncate block'>
                                          {scan.website_id}
                                        </a>
                                      ) : (
                                        <span className='text-caption font-mono text-text-tertiary truncate block'>
                                          {scan.website_id}
                                        </span>
                                      )}
                                    </div>
                                    <span className='text-text-tertiary flex-shrink-0'>{formatDate(scan.start_time || scan.created_at)}</span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {agentStats.length === 0 && <p className='text-body-xs text-text-tertiary text-center py-4'>No agent data available</p>}
              </div>
            </div>

            {/* Recently Launched */}
            <div className='bg-white rounded-2xl border border-border-subtle p-6'>
              <h2 className='text-body-lg font-semibold text-text-primary mb-4'>Recently Launched (7 Days)</h2>
              <div className='space-y-2'>
                {recentAgents.map((scan) => (
                  <div
                    key={scan.scan_id || scan.id}
                    className='flex items-center justify-between p-3 bg-surface-neutral rounded-xl'>
                    <div className='flex-1 min-w-0'>
                      <p className='font-medium text-text-primary text-body-xs truncate'>{scan.title || "Untitled Scan"}</p>
                      <p className='text-caption text-text-tertiary truncate'>
                        {scan.website_name || scan.website_id || "No website"}
                      </p>
                    </div>
                    <div className='text-right ml-4'>
                      <p className='text-body-xs text-text-secondary'>{formatRelativeTime(scan.start_time || scan.created_at)}</p>
                    </div>
                  </div>
                ))}
                {recentAgents.length === 0 && (
                  <p className='text-body-xs text-text-tertiary text-center py-4'>No recent scans in the last 7 days</p>
                )}
              </div>
            </div>
          </div>

          {/* In-Progress Tracker */}
          <div className='bg-white rounded-2xl border border-border-subtle p-6 mt-6'>
            <h2 className='text-body-lg font-semibold text-text-primary mb-4 flex items-center'>
              <span className='relative flex h-2.5 w-2.5 mr-3'>
                <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-severity-medium opacity-75'></span>
                <span className='relative inline-flex rounded-full h-2.5 w-2.5 bg-severity-medium'></span>
              </span>
              In-Progress Tracker
              <span className='ml-2 px-2 py-0.5 text-caption font-semibold bg-severity-medium/10 text-severity-medium rounded-md'>
                {inProgressScans.reduce((acc, g) => acc + g.scans.length, 0)} active
              </span>
            </h2>

            {inProgressScans.length === 0 ? (
              <div className='text-center py-8'>
                <svg
                  className='h-12 w-12 mx-auto text-gray-300 mb-3'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
                <p className='text-body-sm text-text-secondary'>No scans currently in progress</p>
                <p className='text-body-xs text-text-tertiary mt-1'>All client requests have been completed</p>
              </div>
            ) : (
              <div className='space-y-3'>
                {inProgressScans.map((group) => {
                  const isExpanded = expandedInProgressWebsites.has(group.website_id);
                  return (
                    <div key={group.website_id} className='border border-severity-medium/20 rounded-xl overflow-hidden'>
                      <button
                        onClick={() => toggleInProgressWebsite(group.website_id)}
                        className='w-full bg-severity-medium/5 px-4 py-3 flex items-center justify-between hover:bg-severity-medium/10 transition-colors'>
                        <div className='flex items-center'>
                          <span className='relative flex h-2 w-2 mr-3'>
                            <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-severity-medium opacity-75'></span>
                            <span className='relative inline-flex rounded-full h-2 w-2 bg-severity-medium'></span>
                          </span>
                          <h3 className='font-semibold text-text-primary text-body-sm'>{group.website_name}</h3>
                        </div>
                        <div className='flex items-center gap-3'>
                          <span className='text-caption font-semibold text-severity-medium bg-severity-medium/10 px-2 py-0.5 rounded-md'>
                            {group.scans.length} scan{group.scans.length !== 1 ? "s" : ""}
                          </span>
                          <svg
                            className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'>
                            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                          </svg>
                        </div>
                      </button>

                      {/* Scans List - Collapsible */}
                      {isExpanded && (
                        <div className='divide-y divide-border-subtle border-t border-severity-medium/20'>
                          {group.scans.map((scan) => (
                            <div key={scan.scan_id || scan.id} className='px-4 py-3 hover:bg-surface-neutral/50 transition-colors'>
                              <div className='flex items-center justify-between'>
                                <div className='flex items-center flex-1 min-w-0'>
                                  <span className='relative flex h-2 w-2 mr-3 flex-shrink-0'>
                                    <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-severity-medium opacity-75'></span>
                                    <span className='relative inline-flex rounded-full h-2 w-2 bg-severity-medium'></span>
                                  </span>
                                  <div className='min-w-0'>
                                    <p className='font-medium text-text-primary text-body-sm truncate'>
                                      {scan.title || "Untitled Scan"}
                                    </p>
                                    <p className='text-caption text-text-tertiary font-mono truncate'>
                                      {scan.scan_id || scan.id}
                                    </p>
                                    <p className='text-caption text-text-secondary'>Started: {formatDate(scan.start_time)}</p>
                                  </div>
                                </div>
                                <div className='ml-4 flex items-center'>
                                  <div className='text-right mr-3'>
                                    <p className='text-body-sm font-semibold text-severity-medium'>
                                      {getElapsedTime(scan.start_time)}
                                    </p>
                                    <p className='text-caption text-text-tertiary'>elapsed</p>
                                  </div>
                                  <div className='w-16 h-1.5 bg-surface-neutral-subtle rounded-full overflow-hidden'>
                                    <div className='h-full bg-severity-medium rounded-full animate-pulse w-3/4'></div>
                                  </div>
                                </div>
                              </div>

                              {/* Scope URLs section */}
                              {scan.scope_urls_uri && (
                                <div className='mt-2 ml-5 flex items-center gap-3'>
                                  <div className='flex items-center text-xs text-gray-600'>
                                    <svg
                                      className='h-3.5 w-3.5 mr-1 text-gray-400'
                                      fill='none'
                                      viewBox='0 0 24 24'
                                      stroke='currentColor'>
                                      <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1'
                                      />
                                    </svg>
                                    <span className='text-blue-600 font-medium'>Custom URLs provided</span>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadUrls(scan.scan_id, scan.title);
                                    }}
                                    disabled={downloadingScanId === scan.scan_id}
                                    className='inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed'>
                                    {downloadingScanId === scan.scan_id ? (
                                      <>
                                        <svg className='animate-spin h-3 w-3' fill='none' viewBox='0 0 24 24'>
                                          <circle
                                            className='opacity-25'
                                            cx='12'
                                            cy='12'
                                            r='10'
                                            stroke='currentColor'
                                            strokeWidth='4'></circle>
                                          <path
                                            className='opacity-75'
                                            fill='currentColor'
                                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                                        </svg>
                                        Downloading...
                                      </>
                                    ) : (
                                      <>
                                        <svg className='h-3 w-3' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                          <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
                                          />
                                        </svg>
                                        Download TXT
                                      </>
                                    )}
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* All Agent Statistics — Time Series / Table */}
          <div className='bg-white rounded-2xl border border-border-subtle p-6 mt-6'>
            <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
              <h2 className='text-body-lg font-semibold text-text-primary'>All Agent Statistics</h2>
              <div className='inline-flex rounded-xl border border-border-subtle bg-surface-neutral p-0.5'>
                <button
                  onClick={() => setStatsView("timeseries")}
                  className={`px-3 py-1.5 text-body-xs font-semibold rounded-lg transition-colors ${
                    statsView === "timeseries"
                      ? "bg-white text-text-primary shadow-sm"
                      : "text-text-secondary hover:text-text-primary"
                  }`}>
                  Time Series
                </button>
                <button
                  onClick={() => setStatsView("table")}
                  className={`px-3 py-1.5 text-body-xs font-semibold rounded-lg transition-colors ${
                    statsView === "table"
                      ? "bg-white text-text-primary shadow-sm"
                      : "text-text-secondary hover:text-text-primary"
                  }`}>
                  Table
                </button>
              </div>
            </div>

            {statsView === "timeseries" ? (
              <div>
                <p className='text-caption text-text-tertiary mb-3'>
                  Cumulative agent usage over time ({bucketGranularity === "day" ? "daily" : "weekly"} buckets). Agents with fewer than 2 runs are excluded. Hover a chip to highlight; click to toggle.
                </p>
                {plottedAgents.length === 0 || timeSeriesData.length === 0 ? (
                  <p className='text-body-xs text-text-tertiary text-center py-12'>
                    No agents with 2 or more runs in the selected range
                  </p>
                ) : (
                  <>
                    {/* Search + bulk controls */}
                    <div className='flex flex-wrap gap-2 items-center mb-3'>
                      <input
                        type='text'
                        placeholder='Search agents...'
                        value={agentSearchTerm}
                        onChange={(e) => setAgentSearchTerm(e.target.value)}
                        className='flex-1 min-w-[200px] px-3 py-1.5 text-body-xs bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary'
                      />
                      <span className='text-caption text-text-tertiary'>
                        {visibleAgentKeys?.size ?? 0} of {plottedAgents.length} shown
                      </span>
                      <button
                        onClick={showTopN}
                        className='px-2 py-1 text-caption font-semibold bg-accent-purple/10 text-accent-purple rounded-md hover:bg-accent-purple/20 transition-colors'>
                        Top {TOP_N_DEFAULT}
                      </button>
                      <button
                        onClick={showAllAgents}
                        className='px-2 py-1 text-caption font-semibold bg-accent-green/10 text-accent-green rounded-md hover:bg-accent-green/20 transition-colors'>
                        Show All
                      </button>
                      <button
                        onClick={hideAllAgents}
                        className='px-2 py-1 text-caption font-semibold bg-surface-neutral-subtle text-text-secondary rounded-md hover:bg-surface-neutral transition-colors'>
                        Hide All
                      </button>
                    </div>

                    {/* Chart */}
                    <div style={{ height: 420 }}>
                      <ResponsiveContainer width='100%' height='100%'>
                        <LineChart data={timeSeriesData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray='3 3' stroke='rgba(10,16,48,0.06)' />
                          <XAxis
                            dataKey='date'
                            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
                            axisLine={{ stroke: "rgba(10,16,48,0.08)" }}
                            tickLine={false}
                          />
                          <YAxis
                            domain={[0, "auto"]}
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid rgba(10,16,48,0.08)",
                              borderRadius: "12px",
                              fontSize: "12px",
                            }}
                            itemSorter={(item) => -item.value}
                          />
                          {plottedAgents.map((agent) => {
                            const visible = isAgentVisible(agent.key);
                            const dimmed = hoveredAgentKey && hoveredAgentKey !== agent.key;
                            return (
                              <Line
                                key={agent.key}
                                type='linear'
                                dataKey={agent.key}
                                name={agent.title}
                                stroke={agentColorByKey[agent.key]}
                                strokeWidth={hoveredAgentKey === agent.key ? 3 : 2}
                                strokeOpacity={dimmed ? 0.12 : 1}
                                dot={false}
                                hide={!visible}
                                isAnimationActive={false}
                              />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Agent chip toggles */}
                    <div className='mt-4'>
                      <div className='flex flex-wrap gap-1.5 max-h-32 overflow-y-auto scrollbar-thin p-1'>
                        {filteredAgentChips.length === 0 ? (
                          <p className='text-caption text-text-tertiary py-2'>No agents match "{agentSearchTerm}"</p>
                        ) : (
                          filteredAgentChips.map((agent) => {
                            const visible = isAgentVisible(agent.key);
                            const color = agentColorByKey[agent.key];
                            return (
                              <button
                                key={agent.key}
                                onClick={() => toggleAgentVisibility(agent.key)}
                                onMouseEnter={() => visible && setHoveredAgentKey(agent.key)}
                                onMouseLeave={() => setHoveredAgentKey(null)}
                                title={`${agent.title} — ${agent.count} runs`}
                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-caption font-medium border transition-all ${
                                  visible
                                    ? "bg-white text-text-primary border-border-default"
                                    : "bg-surface-neutral-subtle text-text-tertiary border-transparent opacity-60 hover:opacity-100"
                                }`}>
                                <span
                                  className='w-2 h-2 rounded-full flex-shrink-0'
                                  style={{ backgroundColor: visible ? color : "rgba(10,16,48,0.2)" }}
                                />
                                <span className='truncate max-w-[180px]'>{agent.title}</span>
                                <span className='text-text-tertiary'>{agent.count}</span>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className='overflow-x-auto rounded-xl border border-border-subtle'>
                <table className='w-full'>
                  <thead>
                    <tr className='bg-surface-neutral border-b border-border-default'>
                      <th className='w-8 px-2 py-2.5'></th>
                      <th className='px-4 py-2.5 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]'>Agent</th>
                      <th className='px-4 py-2.5 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]'>Scans</th>
                      <th className='px-4 py-2.5 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]'>Websites</th>
                      <th className='px-4 py-2.5 text-left text-caption font-semibold text-text-secondary uppercase tracking-[0.06em]'>Last Run</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-border-subtle'>
                    {agentStats.map((agent) => {
                      const isExpanded = expandedTableAgentKeys.has(agent.key);
                      const sortedScans = [...agent.scans].sort(
                        (a, b) =>
                          new Date(b.start_time || b.created_at) -
                          new Date(a.start_time || a.created_at)
                      );
                      return (
                        <React.Fragment key={agent.key}>
                          <tr
                            onClick={() => toggleTableAgentExpansion(agent.key)}
                            className='hover:bg-surface-neutral/50 transition-colors cursor-pointer'>
                            <td className='px-2 py-3 text-center'>
                              <svg
                                className={`h-4 w-4 inline text-text-tertiary transition-transform ${isExpanded ? "rotate-90" : ""}`}
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'>
                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                              </svg>
                            </td>
                            <td className='px-4 py-3'>
                              <div className='text-body-sm font-medium text-text-primary truncate max-w-[300px]'>{agent.title}</div>
                              <div className='text-caption text-text-tertiary truncate max-w-[300px]'>
                                {agent.key !== agent.title && agent.key}
                              </div>
                            </td>
                            <td className='px-4 py-3'>
                              <span className='inline-block min-w-[24px] px-2 py-0.5 text-body-xs font-semibold rounded-md text-center bg-accent-purple/10 text-accent-purple'>
                                {agent.count}
                              </span>
                            </td>
                            <td className='px-4 py-3 text-body-xs text-text-secondary'>{agent.websiteCount}</td>
                            <td className='px-4 py-3 text-body-xs text-text-secondary'>{formatDate(agent.latestRun)}</td>
                          </tr>
                          {isExpanded && (
                            <tr className='bg-surface-neutral/40'>
                              <td colSpan={5} className='px-4 py-3'>
                                <div className='rounded-lg border border-border-subtle bg-white overflow-hidden'>
                                  <table className='w-full'>
                                    <thead>
                                      <tr className='bg-surface-neutral'>
                                        <th className='px-3 py-2 text-left text-caption font-semibold text-text-tertiary uppercase tracking-wide'>Brand</th>
                                        <th className='px-3 py-2 text-left text-caption font-semibold text-text-tertiary uppercase tracking-wide'>Scan ID</th>
                                        <th className='px-3 py-2 text-left text-caption font-semibold text-text-tertiary uppercase tracking-wide'>Started</th>
                                      </tr>
                                    </thead>
                                    <tbody className='divide-y divide-border-subtle'>
                                      {sortedScans.map((scan, idx) => {
                                        const plan = planByWebsiteId[scan.website_id];
                                        return (
                                          <tr key={scan.scan_id || scan.id || idx}>
                                            <td className='px-3 py-2 text-body-xs text-text-primary'>
                                              <div className='flex items-center gap-2'>
                                                <span className='font-medium truncate max-w-[220px]'>
                                                  {scan.website_name || scan.website_id || "—"}
                                                </span>
                                                {plan && (
                                                  <span className={`px-1.5 py-0.5 text-caption uppercase font-bold rounded-md ${PLAN_STYLES[displayPlan(plan)] || "bg-surface-neutral-subtle text-text-secondary"}`}>
                                                    {PLAN_DISPLAY[displayPlan(plan)] || displayPlan(plan)}
                                                  </span>
                                                )}
                                              </div>
                                              <div className='text-caption text-text-tertiary font-mono truncate max-w-[220px]'>
                                                {scan.website_id}
                                              </div>
                                            </td>
                                            <td className='px-3 py-2 text-caption font-mono truncate max-w-[280px]'>
                                              {scan.scan_id || scan.id ? (
                                                <a
                                                  href={`https://hub.zenyt.ai/scans/${scan.scan_id || scan.id}`}
                                                  target='_blank'
                                                  rel='noopener noreferrer'
                                                  onClick={(e) => e.stopPropagation()}
                                                  className='text-accent-purple hover:underline'>
                                                  {scan.scan_id || scan.id}
                                                </a>
                                              ) : (
                                                <span className='text-text-secondary'>—</span>
                                              )}
                                            </td>
                                            <td className='px-3 py-2 text-body-xs text-text-secondary whitespace-nowrap'>
                                              {formatDate(scan.start_time || scan.created_at)}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                {agentStats.length === 0 && (
                  <p className='text-body-xs text-text-tertiary text-center py-8'>No agent data available for the selected time range</p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default AgentAnalytics;
